import React, { PureComponent } from 'react';
import {
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';

import { IconButton, Tooltip, Zoom, TextField, Button, LinearProgress } from '@mui/material';

import Tile from '../../../common/components/Tile/Tile';
import TableEnhancedContainer from '../../../common/components/TableEnhanced/TableEnhancedContainer';
import ConfirmationDialog from '../../../common/components/ConfirmationDialog/ConfirmationDialog';
import PermissionsCheck from '../../../common/components/PermissionsCheck/PermissionsCheck';
import PermissionsEnum from '../../../common/constants/PermissionsEnum';
import Loading from '../../../common/components/Loading/Loading';

const UploadUpdatePackage = (props) => (
  <Tile className="u-mb-full" title="Upload an update package" helpTag="packages_upload">
    <div className="u-mb-full">
      <TextField
        variant="standard"
        label="Select a .sup (stratec update package) file."
        placeholder="Select a .sup file by clicking the attached button."
        style={{ width: '80%' }}
        value={props.state.supFileName}
      />
      <input
        accept="*"
        id="browseSupInput"
        type="file"
        ref={(input) => {
          props.state.supFileInput = input;
        }}
        style={{ display: 'none' }}
        onChange={props.onClickSupFileBrowse}
      />
      <label htmlFor="browseSupInput">
        <IconButton
          variant="raised"
          data-qa="qa-browseFileButton"
          color="primary"
          component="span"
          size="large"
        >
          <AttachFileIcon />
        </IconButton>
      </label>
    </div>
    <div>
      <Button
        variant="contained"
        data-qa="qa-uploadButton"
        color="primary"
        onClick={props.onClickUpload}
      >
        Upload
      </Button>
    </div>
    <div className="u-pt-full">
      <LinearProgress
        data-qa="qa-uploadProgressBar"
        variant="determinate"
        value={props.state.uploadCompleted}
      />
    </div>
  </Tile>
);

class UpdatePackagesView extends PureComponent {
  constructor(props) {
    super();

    this.styleConfig = {
      width: '10%',
    };

    this.columnsConfig = [
      {
        name: 'Package name',
        key: 'Name',
        type: 'string',
        visible: true,
        filterable: true,
        sortable: true,
      },
      {
        name: 'Description',
        key: 'Description',
        type: 'string',
        visible: true,
        filterable: true,
        sortable: true,
      },
      {
        name: 'Purpose',
        key: 'Purpose',
        type: 'string',
        visible: true,
        filterable: true,
        sortable: true,
      },
      {
        name: 'Target',
        key: 'Target',
        type: 'string',
        visible: false,
        filterable: true,
        sortable: true,
      },
      {
        name: 'Type',
        key: 'Type',
        type: 'string',
        visible: true,
        filterable: true,
        sortable: true,
      },
      {
        name: 'Version',
        key: 'Version',
        type: 'string',
        visible: true,
        filterable: true,
        sortable: true,
      },
      {
        name: 'Target instrument type',
        key: 'InstrumentType',
        type: 'string',
        visible: true,
        filterable: true,
        sortable: true,
      },
      {
        name: 'Qualification attributes',
        key: 'QualificationAttribute',
        type: 'string',
        visible: true,
        filterable: true,
        sortable: true,
      },
      {
        name: 'File size (MB)',
        key: 'FileSize',
        type: 'number',
        visible: true,
        filterable: true,
        sortable: true,
      },
      {
        name: 'Actions',
        key: null,
        type: 'markup',
        style: this.styleConfig,
        markup: (el, col, elIndex, colIndex) => {
          return (
            <div>
              <Tooltip disableInteractive TransitionComponent={Zoom} title="Download">
                <IconButton
                  key={1}
                  size="large"
                  style={{}}
                  onClick={() => {
                    return props.onClickDownload(el);
                  }}
                  data-qa="qa-download"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <PermissionsCheck permission={PermissionsEnum.RELEASE_AUTHORITY}>
                {!this.props.supVisibilityByRegionsRestriction ||
                this.props.userPermissions.includes(PermissionsEnum.DELETE_ALL_SUP_FILES) ||
                this.props.userRegionId == el.RegionId ? (
                  <Tooltip disableInteractive TransitionComponent={Zoom} title="Delete">
                    <IconButton
                      size="large"
                      key={2}
                      style={{}}
                      onClick={(event) => {
                        return props.onClickDelete(event, el);
                      }}
                      data-qa="qa-delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </PermissionsCheck>
            </div>
          );
        },
        visible: true,
        filterable: false,
        sortable: false,
      },
    ];
  }

  _addRegionToConfig = (columnsConfig, newColumnObject) => {
    const updatedColumns = [...columnsConfig];
    const penultimateIndex = updatedColumns.length - 1;
    updatedColumns.splice(penultimateIndex, 0, newColumnObject);
    return updatedColumns;
  };

  _getRegionColumn = (props) => {
    return {
      name: 'Region',
      key: 'RegionId',
      type: 'markup',
      markup: (el, col, elIndex, colIndex) => {
        let region = props.state.regions.find((r) => r.RegionId === el.RegionId);
        return <div>{region ? region.Name : null}</div>;
      },
      style: { width: '8rem', marginTop: '6px' },
      visible: false,
      filterable: true,
      sortable: true,
    };
  };

  render() {
    return (
      <div>
        <ConfirmationDialog
          onClickCancel={this.props.showConfirmDialog.cancel}
          onClickMainAction={this.props.showConfirmDialog.action}
          open={this.props.showConfirmDialog.show}
          messageToConfirm={this.props.messageToConfirm}
          title={this.props.showConfirmDialog.title}
          mainActionLabel={this.props.showConfirmDialog.mainActionLabel}
          disableMainAction={this.props.showConfirmDialog.disableMainAction}
        />
        <PermissionsCheck permission={PermissionsEnum.RELEASE_AUTHORITY}>
          <UploadUpdatePackage {...this.props} />
        </PermissionsCheck>
        <Loading condition={this.props.isFetching}>
          <Tile title="Available update packages" helpTag="packages_available_packages">
            <TableEnhancedContainer
              data={this.props.sortPackages(this.props.state.updateList)}
              columns={
                this.props.supVisibilityByRegionsRestriction
                  ? this._addRegionToConfig(this.columnsConfig, this._getRegionColumn(this.props))
                  : this.columnsConfig
              }
              enableHideColumns={true}
              enableActionButtons={false}
            />
          </Tile>
        </Loading>
      </div>
    );
  }
}

export default UpdatePackagesView;
